<template>
  <div class="availability-form-daily-item">
    <div class="grid grid-cols-12 items-center gap-x-2">
      <!-- Day label -->
      <span v-if="index === 0" class="col-span-4 text-sm font-semibold select-none">
        {{ recurringAvailability.getDayOfWeekDisplay() }}
      </span>

      <!-- Time slot -->
      <div class="col-span-6 grid grid-cols-12 gap-x-2" :class="{ 'col-start-5': index > 0 }">
        <div class="col-span-12 flex h-6.5 flex-wrap items-center gap-x-2">
          <span v-if="!isAvailableOnThisDay" class="text-sm select-none"> Niet beschikbaar </span>
          <InputTimepicker
            v-if="isAvailableOnThisDay"
            v-model="recurringAvailability.timeStart"
            :disabled="
              recurringAvailability.availabilityType !== AvailabilityTypes.AVAILABLE || disabled
            "
            time-only
            @focusout="onFocusOut"
          />
          <InputTimepicker
            v-if="isAvailableOnThisDay"
            v-model="recurringAvailability.timeEnd"
            :disabled="
              recurringAvailability.availabilityType !== AvailabilityTypes.AVAILABLE || disabled
            "
            time-only
            @focusout="onFocusOut"
          />
          <GeneralButton
            v-if="isLast && recurringAvailability.isComplete()"
            variant="text"
            :icon="['fas', 'plus']"
            :disabled="
              recurringAvailability.availabilityType !== AvailabilityTypes.AVAILABLE ||
              disabled ||
              !canAdd
            "
            title="Tijdslot toevoegen"
            class="p-0!"
            @click="$emit('addRecurringAvailability', recurringAvailability)"
          />
          <GeneralButton
            v-if="!isLast || !isFirst"
            variant="text"
            :icon="['fas', 'minus']"
            :disabled="
              recurringAvailability.availabilityType !== AvailabilityTypes.AVAILABLE ||
              disabled ||
              isBusy
            "
            title="Tijdslot verwijderen"
            class="p-0!"
            @click="onClickRemove"
          />
        </div>
        <div v-if="errorMessage" class="col-span-12">
          <span class="text-error text-sm select-none">
            {{ errorMessage }}
          </span>
        </div>
      </div>

      <!-- Active switch (only shown on the first time slot for the day) -->
      <div v-if="isFirst" class="col-span-2 flex justify-end">
        <InputSwitch
          v-model="isAvailableOnThisDay"
          :title="`${recurringAvailability.getDayOfWeekDisplay()} beschikbaar`"
          :disabled="disabled"
          @update:model-value="onUpdateDayAvailability"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue"
import useVuelidate from "@vuelidate/core"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import InputSwitch from "@/components/inputs/InputSwitch.vue"
import InputTimepicker from "@/components/inputs/InputTimepicker.vue"
import { RecurringAvailability } from "@/models/recurring-availability.model.ts"
import { AvailabilityTypes } from "@/enums/models/availability-types.ts"
import useDelayedFunction from "@/composables/input/delayed-function.ts"
import {
  chronologicalStartAndEndTimeValidator,
  timeNoOverlapValidator
} from "@/helper/validation/recurring-availability-validation.ts"
import type { RecurringAvailabilityController } from "@/controllers/model-controllers/recurring-availability-controller.ts"
import useEntityUpdater from "@/composables/api/entity-updater.ts"

const recurringAvailability = defineModel<RecurringAvailability>({ required: true })

const isAvailableOnThisDay = ref(
  recurringAvailability.value.availabilityType === AvailabilityTypes.AVAILABLE
)

const recurringAvailabilityController = inject<RecurringAvailabilityController>(
  "recurringAvailabilityController"
)!
const entityUpdater = useEntityUpdater(
  recurringAvailability,
  recurringAvailabilityController.update,
  recurringAvailabilityController.create
)

function updateIfValid() {
  if (!v$.value.$invalid) {
    entityUpdater.update()
  }
}
const delayedUpdate = useDelayedFunction(updateIfValid, 300)
const { isBusy } = entityUpdater

const props = defineProps<{
  index: number
  isFirst: boolean
  isLast: boolean
  canAdd: boolean
  disabled: boolean
  allRecurringAvailabilities: RecurringAvailability[]
}>()

const emit = defineEmits(["addRecurringAvailability", "removeRecurringAvailability"])

function onClickRemove() {
  if (isBusy.value) {
    return
  }

  delayedUpdate.cancel()

  if (recurringAvailability.value.isSaved) {
    // Removing saved by controller will update parent collection automatically.
    recurringAvailabilityController.deleteOrRollback(recurringAvailability.value)
  } else {
    // Local (unsaved) entity. Tell the parent to remove it.
    emit("removeRecurringAvailability", recurringAvailability.value)
  }

  // Re-validate after DOM has updated, or validation errors on the deleted will not be cleared.
  setTimeout(v$.value.$touch)
}

async function onUpdateDayAvailability(isAvailable: boolean) {
  if (isAvailable) {
    // Update existing recurring availability to available
    recurringAvailability.value.availabilityType = AvailabilityTypes.AVAILABLE
    await recurringAvailabilityController.update(recurringAvailability.value)
  } else {
    // Delete all recurring availabilities for this day except one
    const [remaining, ...toDelete] = props.allRecurringAvailabilities

    if (toDelete.length > 0) {
      await recurringAvailabilityController.bulkDelete(toDelete)
    }

    // Update the remaining recurring availability to unavailable
    remaining.availabilityType = AvailabilityTypes.UNAVAILABLE
    remaining.timeStart = null
    remaining.timeEnd = null
    await entityUpdater.update(remaining)
  }
}

function onFocusOut() {
  v$.value.$touch()
  delayedUpdate.schedule()
}

// --- Validation ---

const validationRules = computed(() => ({
  range: { timeRange: chronologicalStartAndEndTimeValidator() },
  overlap: { noOverlap: timeNoOverlapValidator(props.allRecurringAvailabilities) }
}))

const validationState = computed(() => ({
  range: recurringAvailability.value,
  overlap: recurringAvailability.value
}))

const v$ = useVuelidate(validationRules, validationState)

const errorMessage = computed(() => {
  if (
    props.disabled ||
    recurringAvailability.value.availabilityType !== AvailabilityTypes.AVAILABLE
  ) {
    return null
  }

  return (
    v$.value.timeStart?.$errors?.[0]?.$message ||
    v$.value.timeEnd?.$errors?.[0]?.$message ||
    v$.value.range?.$errors?.[0]?.$message ||
    v$.value.overlap?.$errors?.[0]?.$message ||
    null
  )
})
</script>
